import { ActivationAttempt, GameState, Player, PreparedCard, CardType, EffectResult, DamageAttempt } from '../types'
import { getCardEffect } from './gameLogic'
import { getImmediateDamageAmount, isSpikesCard } from './damageRules'

/**
 * PHASE 2: Centralized Effect Resolution System (Option B)
 * 
 * This module implements tree-aware effect resolution with priority rules.
 * 
 * Key concepts:
 * - EffectResult: unified representation of all card effects
 * - Priority rules: earlier child effects > later child effects
 * - Cancellation: higher-priority effects can cancel lower-priority ones
 * 
 * Flow:
 * 1. buildEffectResult() - Convert activation to EffectResult
 * 2. applyEffectPriorities() - Apply tree-aware cancellation rules
 * 3. executeEffect() - Apply uncancelled effects to game state
 */

// Logging disabled to improve FPS
const EFFECT_RESOLUTION_DEBUG = false

const log = (tag: string, message: string, data?: any) => {
    if (!EFFECT_RESOLUTION_DEBUG) return
    if (data) {
        console.log(`[${tag}] ${message}`, data)
    } else {
        console.log(`[${tag}] ${message}`)
    }
}

// Helper to identify Spikes card
const isSpikes = (preparedCard: PreparedCard): boolean => {
    const card = preparedCard.card
    const result = (
        card.type === CardType.Defense &&
        card.subType === 'Armory' &&
        (
            (card as any).faceSlug === 'spikes' ||
            (card.extendedShield && card.value === 0 && card.essenceCost === 6)
        )
    )

    // Debug logging
    if (card.type === CardType.Defense && card.subType === 'Armory') {
        log('isSpikes', `Checking Defense/Armory card: faceSlug="${(card as any).faceSlug}", extendedShield=${card.extendedShield}, value=${card.value}, cost=${card.essenceCost} → isSpikes=${result}`)
    }

    return result
}

/**
 * Interface for activation tree nodes (imported concepts from activations.ts)
 * Redefined here to avoid circular dependency
 */
export interface ActivationNode {
    index: number
    activation: ActivationAttempt
    parent: ActivationNode | null
    children: ActivationNode[]
    isActive: boolean  // !wasCountered && winningCard exists
}

/**
 * Build EffectResult from a winning card
 * 
 * Maps card types to their effect representations.
 * Returns null for cards that don't need tree-aware handling.
 */
export const buildEffectResult = (
    winningCard: PreparedCard,
    activation: ActivationAttempt,
    activationIndex: number,
    affectedChains: number[],
    state: GameState
): EffectResult | null => {
    const cardType = getCardEffect(winningCard).type
    const cardValue = getCardEffect(winningCard).value
    const ownerId = winningCard.card.ownerId!
    const targetId = activation.targetId ?? (ownerId === 1 ? 2 : 1)

    log('buildEffectResult', `Building effect for ${cardType} at activation[${activationIndex}]`, {
        ownerId,
        targetId,
        affectedChains
    })

    // Map card types to effect types
    switch (cardType) {
        case CardType.Invincible:
        case CardType.Hibernation:  // Hibernation blocks all damage like Invincible
            return {
                activationIndex,
                cardType,
                ownerId,
                targetId,
                affectedChains,
                effectType: 'IMMUNITY',
                cancelled: false
            }

        case CardType.Counterblast:
            return {
                activationIndex,
                cardType,
                ownerId,
                targetId,
                affectedChains,
                effectType: 'REFLECTION',
                cancelled: false
            }

        case CardType.Defense: {
            // Check if this is Spikes
            if (isSpikesCard(winningCard)) {
                return {
                    activationIndex,
                    cardType,
                    ownerId,
                    targetId,
                    affectedChains,
                    effectType: 'DAMAGE',  // Spikes deals damage
                    damage: 0,  // Will be calculated in applyEffectPriorities
                    cancelled: false
                }
            }
            // Regular defense - not tree-aware, return null
            return null
        }

        case CardType.Attack:
        case CardType.InfernoGauntlet:
        case CardType.Larcendagger:
        case CardType.EnergySword:
        case CardType.FlyingScythe:
        case CardType.LifeDrain:
        case CardType.Chromabow:
        case CardType.Trembling:
        case CardType.PlantedBomb:
            return {
                activationIndex,
                cardType,
                ownerId,
                targetId,
                affectedChains,
                effectType: 'DAMAGE',
                damage: getImmediateDamageAmount(winningCard, activation, state),
                cancelled: false
            }

        case CardType.Protector: {
            // Check rotation to determine if attack or defense mode
            const rotation = (winningCard.rotation % 360 + 360) % 360
            const isAttackMode = rotation > 90 && rotation < 270

            if (isAttackMode) {
                return {
                    activationIndex,
                    cardType,
                    ownerId,
                    targetId,
                    affectedChains,
                    effectType: 'DAMAGE',
                    damage: cardValue,
                    cancelled: false
                }
            }
            // Defense mode - not tree-aware
            return null
        }

        default:
            // Non-tree-aware effects return null (handled by applyCardEffect)
            return null
    }
}

/**
 * Apply tree-aware priority rules to effect results
 * 
 * Rules:
 * 1. Invincible cancels ALL damage targeting its owner in affected chains
 * 2. Counterblast cancels damage and creates reflection
 * 3. Spikes calculates damage based on # of damage attempts in parent chain
 * 
 * Priority: Earlier child branch > Later child branch (determined by affectedChains)
 */
export const applyEffectPriorities = (
    effects: EffectResult[],
    tree: ActivationNode[],
    state: GameState
): void => {
    log('applyEffectPriorities', `Processing ${effects.length} effects with priority rules`)

    // Step 1: Find all Invincible/Hibernation effects (both block damage)
    const invincibleEffects = effects.filter(e =>
        e.effectType === 'IMMUNITY' && !e.cancelled
    )

    log('applyEffectPriorities', `Found ${invincibleEffects.length} Immunity effects (Invincible/Hibernation)`,
        invincibleEffects.map(e => ({ activationIndex: e.activationIndex, cardType: e.cardType, ownerId: e.ownerId })))

    // For each Immunity effect, PREVENT (not cancel) damage in its affected chains
    // Prevented damage still exists for reflection purposes, but won't be dealt to the player
    invincibleEffects.forEach(invincible => {
        effects.forEach(effect => {
            // Immunity protects its owner from damage
            if (effect.effectType === 'DAMAGE' &&
                effect.targetId === invincible.ownerId &&
                invincible.affectedChains.includes(effect.activationIndex)) {

                log('applyEffectPriorities',
                    `${invincible.cardType}[${invincible.activationIndex}] prevents damage from [${effect.activationIndex}]`,
                    { damage: effect.damage, cardType: effect.cardType })

                // CRITICAL: Use 'prevented', NOT 'cancelled'
                // This allows Counterblast to still see and reflect the damage
                effect.prevented = true
                effect.damagePrevented = true // NEW: Explicitly mark damage as prevented
                effect.preventedBy = invincible.activationIndex
                // Do NOT set cancelled = true!
            }
        })
    })

    // Step 2: Calculate Spikes damage BEFORE Counterblast
    // Spikes damage = (all damage cards used this night + current chain damage attempts) × 2
    const spikesEffects = effects.filter(e =>
        e.cardType === CardType.Defense && e.effectType === 'DAMAGE' && !e.cancelled
    )

    log('applyEffectPriorities', `Found ${spikesEffects.length} Spikes effects`,
        spikesEffects.map(e => ({ activationIndex: e.activationIndex, ownerId: e.ownerId })))

    spikesEffects.forEach(spikes => {
        // Count damage cards attempted this night. Spikes cares about attempts, not only
        // damage that ultimately got through.
        const damageMapThisNight = state.attemptedDamageCardsThisNight || state.damageCardsUsedThisNight || {}
        let priorDamageCardCount = 0

        // Count all damage cards that targeted the Spikes owner this night
        Object.entries(damageMapThisNight).forEach(([key, count]) => {
            // Key format: "attackerId->defenderId"
            const parts = key.split('->')
            if (parts.length === 2) {
                const defenderId = parseInt(parts[1], 10)
                if (defenderId === spikes.ownerId) {
                    priorDamageCardCount += count
                }
            }
        })

        // Count damage attempts in current activation chain (if Spikes is used as counter)
        const currentChainDamageAttempts = effects.filter(e =>
            e.effectType === 'DAMAGE' &&
            e.targetId === spikes.ownerId &&
            spikes.affectedChains.includes(e.activationIndex) &&
            e.cardType !== CardType.Defense &&
            e.cardType !== CardType.Counterblast
        )

        const currentChainCount = currentChainDamageAttempts.length
        const untrackedCurrentCount = currentChainDamageAttempts.filter(e => e.activationIndex !== spikes.activationIndex).length
        const totalDamageCards = priorDamageCardCount + untrackedCurrentCount
        const spikesDamage = totalDamageCards * 2  // 2 damage per damage card
        const reflectedTarget = currentChainDamageAttempts[0]?.ownerId ??
            state.players.find(player => player.id !== spikes.ownerId)?.id

        log('applyEffectPriorities',
            `Spikes[${spikes.activationIndex}] counted ${priorDamageCardCount} prior + ${currentChainCount} current = ${totalDamageCards} total = ${spikesDamage} damage`,
            currentChainDamageAttempts.map(d => ({ activationIndex: d.activationIndex, cardType: d.cardType, cancelled: d.cancelled })))

        spikes.damage = spikesDamage
        if (reflectedTarget !== undefined) {
            spikes.targetId = reflectedTarget
        }
    })

    // Step 3: Find all Counterblast effects (AFTER Spikes damage is calculated)
    const counterblastEffects = effects.filter(e =>
        e.effectType === 'REFLECTION' && !e.cancelled
    )

    log('applyEffectPriorities', `Found ${counterblastEffects.length} Counterblast effects`,
        counterblastEffects.map(e => ({ activationIndex: e.activationIndex, ownerId: e.ownerId })))

    // For each Counterblast, prevent incoming damage and store reflected damage on the Counterblast's own effectResult
    counterblastEffects.forEach(counterblast => {
        const sourceActivationIndex = tree[counterblast.activationIndex]?.activation.parentActivationIndex
        if (sourceActivationIndex === undefined) {
            return
        }

        // Find damage targeting the Counterblast owner only in the selected parent chain.
        const incomingDamage = effects.filter(e =>
            e.effectType === 'DAMAGE' &&
            e.targetId === counterblast.ownerId &&
            !e.cancelled &&
            e.activationIndex === sourceActivationIndex
        )

        log('applyEffectPriorities',
            `Counterblast[${counterblast.activationIndex}] found ${incomingDamage.length} incoming damage`,
            incomingDamage.map(d => ({ activationIndex: d.activationIndex, damage: d.damage, cardType: d.cardType })))

        let totalReflectedDamage = 0
        let reflectedTargetId: number | undefined

        incomingDamage.forEach(dmg => {
            // PREVENT the incoming damage (don't cancel it)
            // This ensures the damage value remains visible for other effects
            log('applyEffectPriorities',
                `Counterblast[${counterblast.activationIndex}] prevents and reflects ${dmg.damage} damage from [${dmg.activationIndex}]`)

            dmg.prevented = true
            dmg.damagePrevented = true
            dmg.preventedBy = counterblast.activationIndex

            // Accumulate reflected damage on the Counterblast's own effectResult
            totalReflectedDamage += (dmg.damage || 0)
            reflectedTargetId = dmg.ownerId  // Reflect back to attacker
        })

        // Store reflected damage directly on the Counterblast effectResult
        // so applyCardEffect can read it via the effectResult parameter
        if (totalReflectedDamage > 0 && reflectedTargetId !== undefined) {
            counterblast.damage = totalReflectedDamage
            counterblast.targetId = reflectedTargetId
            log('applyEffectPriorities',
                `Counterblast[${counterblast.activationIndex}] stored ${totalReflectedDamage} reflected damage targeting player ${reflectedTargetId}`)
        }
    })

    log('applyEffectPriorities', 'Priority rules applied. Final effects:',
        effects.map(e => ({
            activationIndex: e.activationIndex,
            effectType: e.effectType,
            cardType: e.cardType,
            damage: e.damage,
            cancelled: e.cancelled,
            cancelReason: e.cancelReason
        })))
}

/**
 * Execute a single effect (stub for Phase 3 integration)
 * 
 * This will be implemented in Phase 3 when integrating with finalResolution.
 * For now, returns state unchanged.
 */
export const executeEffect = (
    result: EffectResult,
    state: GameState
): GameState => {
    // Phase 3: This will be implemented during integration
    // For now, just return state unchanged
    log('executeEffect', 'Effect execution not yet implemented (Phase 3)')
    return state
}
